import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { me } from '../api/me';
import { useAuth } from './AuthContext';
import { THEME_STORAGE_KEY, ThemeContext, type Theme, type ThemeContextValue } from './ThemeContext';

const DARK_QUERY = '(prefers-color-scheme: dark)';

// Storage and matchMedia can be missing or throw (private browsing, blocked
// site data, old browsers). Without them the device setting, then Lightbox,
// decides, and a choice lasts until the page is reloaded.
function storedChoice(): Theme | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

function storeChoice(theme: Theme): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Not remembered on this device.
  }
}

function deviceQuery(): MediaQueryList | null {
  try {
    return window.matchMedia(DARK_QUERY);
  } catch {
    return null;
  }
}

/**
 * Lightbox or Darkroom. The device setting decides until the customer picks
 * one; their pick is kept on this device and, once they're signed in, on
 * their profile, so it follows them to other devices. A profile choice wins
 * over this device's, and a choice made before signing in is saved to a
 * profile that has none.
 *
 * public/theme-init.js applies the same rule before first paint; this keeps
 * `<html data-theme>` in step from then on.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { authenticated, loading } = useAuth();
  const [choice, setChoice] = useState<Theme | null>(storedChoice);
  const [device, setDevice] = useState<Theme>(() => (deviceQuery()?.matches ? 'dark' : 'light'));
  const theme = choice ?? device;

  useEffect(() => {
    const query = deviceQuery();
    if (!query) return;
    const onChange = (event: MediaQueryListEvent) => setDevice(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    if (loading || !authenticated) return;
    let cancelled = false;
    me.get()
      .then((profile) => {
        if (cancelled) return;
        const saved = profile.preferences?.theme ?? null;
        if (saved) {
          setChoice(saved);
          storeChoice(saved);
          return;
        }
        const local = storedChoice();
        if (local) me.setTheme(local).catch(() => {});
      })
      // Unreachable profile: this device's choice stands.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authenticated, loading]);

  const setTheme = useCallback(
    (next: Theme) => {
      setChoice(next);
      storeChoice(next);
      // Kept on this device even if saving to the profile fails.
      if (authenticated) me.setTheme(next).catch(() => {});
    },
    [authenticated],
  );

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
